// conversationMetaCache — the persistent-unread loader's memory of `conversations.meta`
// (components/NotificationHandler, Supabase path).
//
// Why (measured 2026-09-24, production logs): the loader re-read `id, meta` for EVERY watched
// conversation (~4,800 order/direct/card threads, 32 chunked `in` queries + as many CORS
// preflights) each time its key changed — and the key includes the viewer's read times, so
// every chat read in every office tab re-downloaded everything: ~40 % of all API traffic and the
// 20 s stall bursts (522) that also failed login token refreshes.
//
// Cache spec:
//   key          conversation id, scoped to the signed-in user (forUser clears on a change)
//   value        `conversations.meta` as stored (never written back; the table is the truth)
//   freshness    fetched once; `invalidate(id)` when the live `messages` feed sees a new row
//                in that thread; entries older than maxAgeMs are refetched lazily (safety net
//                for a missed live event), with per-tab jitter so tabs do not refresh together
//   read times   need no fetch: badges are recomputed from memory (computePersistentUnread)
//   absent rows  an id with no conversation row yet is remembered as `{}` (no refetch storm)
//   failure      successful chunks are kept; a failed chunk backs off 30 s → 5 min (jittered)
//   stampede     an id already in flight is never requested twice
//   memory       prune(ids) drops entries the viewer no longer watches
import { UserRole } from '../types';

export interface ConvMetaRow { id: string; meta: any }
export type ChunkResult = { data: any[] | null; error: any };
export type ChunkFetcher = (ids: string[]) => Promise<ChunkResult>;
export type RefreshResult = 'none' | 'fetched' | 'error' | 'backoff';

/** PostgREST `in` list size — the URL limit (2026-09-10: an unchunked list answered 400). */
export const META_CHUNK = 150;
/** Safety refresh for entries a missed live event could have left stale. */
export const META_MAX_AGE_MS = 10 * 60 * 1000;
export const META_RETRY_MIN_MS = 30_000;
export const META_RETRY_MAX_MS = 5 * 60 * 1000;

export class ConversationMetaCache {
  private userId = '';
  private entries = new Map<string, { meta: any; at: number }>();
  private inflight = new Set<string>();
  private failures = 0;
  private retryAt = 0;
  /** The last error message a refresh met (for the caller's log line). */
  lastError = '';

  constructor(
    private readonly fetchChunk: ChunkFetcher,
    private readonly now: () => number = () => Date.now(),
    private readonly maxAgeMs: number = META_MAX_AGE_MS,
    private readonly random: () => number = Math.random,
  ) {}

  /** Scope the cache to a user; a different user starts empty. */
  forUser(userId: string): void {
    if (userId === this.userId) return;
    this.userId = userId;
    this.entries.clear();
    this.inflight.clear();
    this.failures = 0;
    this.retryAt = 0;
  }

  /** A new message landed in this thread: its meta changed, fetch it again next time. */
  invalidate(id: string): void { this.entries.delete(id); }

  /** Forget threads the viewer no longer watches. */
  prune(keep: Set<string>): void {
    for (const id of [...this.entries.keys()]) if (!keep.has(id)) this.entries.delete(id);
  }

  /** Every id has an entry (fresh or stale). */
  covers(ids: string[]): boolean { return ids.every((id) => this.entries.has(id)); }

  /** Cached rows for these ids, in order (ids without an entry are skipped). */
  rows(ids: string[]): ConvMetaRow[] {
    const out: ConvMetaRow[] = [];
    for (const id of ids) {
      const e = this.entries.get(id);
      if (e) out.push({ id, meta: e.meta });
    }
    return out;
  }

  /**
   * Fetch the ids that are missing or older than maxAgeMs and not already in flight. Chunks are
   * STARTED synchronously, in order (the notification layer relies on its reads being issued
   * before later effects' reads). Resolves with what happened.
   */
  async refresh(ids: string[]): Promise<RefreshResult> {
    const now = this.now();
    const need = ids.filter((id) => {
      if (this.inflight.has(id)) return false;
      const e = this.entries.get(id);
      return !e || now - e.at >= this.maxAgeMs;
    });
    if (need.length === 0) return 'none';
    if (now < this.retryAt) return 'backoff';

    const user = this.userId;
    for (const id of need) this.inflight.add(id);
    const chunks: string[][] = [];
    for (let i = 0; i < need.length; i += META_CHUNK) chunks.push(need.slice(i, i + META_CHUNK));
    const started = chunks.map((chunk) =>
      this.fetchChunk(chunk).then(
        (r) => ({ chunk, r }),
        (e) => ({ chunk, r: { data: null, error: e } as ChunkResult }),
      ));
    const results = await Promise.all(started);
    for (const id of need) this.inflight.delete(id);
    if (this.userId !== user) return 'none'; // signed out / switched while loading: drop it

    const at = this.now();
    let failed: any = null;
    for (const { chunk, r } of results) {
      if (r?.error) { failed = failed ?? r.error; continue; }
      const seen = new Set<string>();
      for (const row of r?.data || []) {
        const id = String(row?.id ?? '');
        if (!id) continue;
        seen.add(id);
        this.entries.set(id, { meta: row.meta || {}, at });
      }
      // No conversation row yet (an order nobody has written about): remember that too.
      for (const id of chunk) if (!seen.has(id)) this.entries.set(id, { meta: {}, at });
    }
    if (failed) {
      this.failures += 1;
      const base = Math.min(META_RETRY_MAX_MS, META_RETRY_MIN_MS * 2 ** (this.failures - 1));
      this.retryAt = at + Math.round(base * (0.75 + this.random() * 0.5));
      this.lastError = String(failed?.message || failed);
      return 'error';
    }
    this.failures = 0;
    this.retryAt = 0;
    return 'fetched';
  }
}

export interface PersistentUnreadInfo {
  orderId: string;
  channel: string;
  channels: string[];
  channelCounts: Record<string, number>;
  senderName: string;
  lastMessage: string;
  timestamp: number;
  count: number;
}

/**
 * Unread from stored meta — moved verbatim from NotificationHandler's persistent-unread load
 * (2026-09-24) so the cached path computes exactly what the old network path did.
 */
export function computePersistentUnread(
  rows: ConvMetaRow[],
  lastReadChat: Record<string, Record<string, number>>,
  viewer: { id: string; role?: string },
): Record<string, PersistentUnreadInfo> {
  const unread: Record<string, PersistentUnreadInfo> = {};
  for (const rowItem of rows || []) {
    const data: any = (rowItem.meta as any) || {};
    const convId = rowItem.id as string;
    const convLastRead = lastReadChat[convId] || {};
    const isDirect = convId.startsWith('direct_');
    // Order-Board text threads talk to the customer on the customer
    // channel — that IS the thread, for the staff working the board.
    const isCard = convId.startsWith('card_');
    const channels: string[] = [];

    const roleKey = viewer.role;
    const channelsToCheck = isDirect
      ? ['dispatch']
      : isCard
      // Board threads have BOTH tabs in use: customer texting and the
      // staff-side DRIVER tab (dispatch channel).
      ? ['customer', 'dispatch']
      : roleKey === UserRole.DRIVER
        ? ['customer', 'dispatch', 'driver_store']
        : roleKey === UserRole.STORE
          ? ['admin_store', 'driver_store']
          : roleKey === UserRole.WAREHOUSE_MANAGER || roleKey === UserRole.WORKER || roleKey === UserRole.PACKAGER
            ? [] // no order-channel unread for warehouse-side roles
            : ['dispatch', 'admin_store']; // admin-tier (super_admin / admin / dispatch)

    for (const ch of channelsToCheck) {
      const lastMsgTime = data.lastMessagePerChannel?.[ch] || 0;
      const lastReadTime = convLastRead[ch] || 0;
      const lastSender = data.lastSenderPerChannel?.[ch];
      if (lastMsgTime > lastReadTime && lastSender !== viewer.id) {
        channels.push(ch);
      }
    }

    if (channels.length > 0) {
      const channelCounts: Record<string, number> = {};
      for (const ch of channels) channelCounts[ch] = 1;
      unread[convId] = {
        orderId: convId,
        channel: channels[0],
        channels,
        channelCounts,
        senderName: data.lastSenderName || '',
        lastMessage: data.lastMessageBody || 'Unread messages',
        timestamp: data.lastMessageAt || 0,
        count: channels.length,
      };
    }
  }
  return unread;
}
